import React, { useState, useEffect } from 'react';
import {
  Plus,
  Trash2,
  Save,
  GripVertical,
  MoreVertical,
  Star,
  Eye,
  Loader2
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { createTemplate, updateTemplate } from '../../../services/templateService';

const arrayMove = (arr, from, to) => {
  const newArr = [...arr];
  const [item] = newArr.splice(from, 1);
  newArr.splice(to, 0, item);
  return newArr;
};

// --- Default Data Sets ---

const APPRAISAL_DEFAULT_SECTIONS = [
  {
    id: 'sec_1',
    title: "Core Values",
    description: "Evaluate the employee's adherence to company core values.",
    weight: 50,
    questions: [
      { id: 'q_1', text: "Attention to detail in work outputs" },
      { id: 'q_2', text: "Ability to meet project specifications and deadlines" },
      { id: 'q_3', text: "Consistency in maintaining high-quality work" }
    ]
  },
  {
    id: 'sec_2',
    title: "Professional Skills",
    description: "Assess technical and professional capabilities.",
    weight: 50,
    questions: [
      { id: 'q_4', text: "Technical knowledge required for the role" },
      { id: 'q_5', text: "Problem-solving capabilities" }
    ]
  }
];

const EXIT_DEFAULT_SECTIONS = [
  {
    id: 'exit_1',
    title: "Role & Job Satisfaction",
    description: "Feedback on the actual role versus expectations.",
    weight: 20,
    questions: [
      { id: 'eq_1', text: "My role matched what was promised during hiring" },
      { id: 'eq_2', text: "My workload was manageable" },
      { id: 'eq_3', text: "My skills were fully utilized" }
    ]
  },
  {
    id: 'exit_2',
    title: "Management & Leadership",
    description: "Feedback on reporting manager and leadership.",
    weight: 20,
    questions: [
      { id: 'eq_4', text: "My manager supported my professional growth" },
      { id: 'eq_5', text: "Performance evaluations were fair" }
    ]
  },
  {
    id: 'exit_3',
    title: "Compensation & Benefits",
    description: "Satisfaction with pay and perks.",
    weight: 20,
    questions: [
      { id: 'eq_6', text: "Compensation was fair for my responsibilities" },
      { id: 'eq_7', text: "Benefits met my expectations" }
    ]
  },
  {
    id: 'exit_4',
    title: "Work Culture",
    description: "Environment and team dynamics.",
    weight: 20,
    questions: [
      { id: 'eq_8', text: "I felt comfortable raising concerns" },
      { id: 'eq_9', text: "Team collaboration was effective" }
    ]
  },
  {
    id: 'exit_5',
    title: "Career Growth",
    description: "Opportunities for advancement.",
    weight: 20,
    questions: [
      { id: 'eq_10', text: "Clear career path existed" },
      { id: 'eq_11', text: "Training opportunities were sufficient" }
    ]
  }
];

const APPRAISAL_RATING_OPTIONS = [
  "Outstanding",
  "Exceeds Expectations",
  "Meets Expectations",
  "Needs Improvement",
  "Unacceptable"
];

const EXIT_RATING_OPTIONS = [
  "Strongly Disagree",
  "Disagree",
  "Neutral",
  "Agree",
  "Strongly Agree",
];

const PIP_DEFAULT_SECTIONS = [
  {
    id: 'pip_1',
    title: "Performance Gap Areas",
    description: "Identify specific areas where performance falls below expectations.",
    weight: 25,
    questions: [
      { id: 'pq_1', text: "Quality of deliverables meets the required standard" },
      { id: 'pq_2', text: "Adherence to deadlines and timelines" },
      { id: 'pq_3', text: "Accuracy and attention to detail in assigned tasks" }
    ]
  },
  {
    id: 'pip_2',
    title: "SMART Goals Progress",
    description: "Evaluate progress toward specific, measurable improvement goals.",
    weight: 30,
    questions: [
      { id: 'pq_4', text: "Goal completion rate within defined timeframe" },
      { id: 'pq_5', text: "Demonstrates measurable improvement in KPIs" },
      { id: 'pq_6', text: "Takes initiative to seek feedback and course-correct" }
    ]
  },
  {
    id: 'pip_3',
    title: "Behavioral & Professional Conduct",
    description: "Assess attitude, accountability, and professional behavior.",
    weight: 20,
    questions: [
      { id: 'pq_7', text: "Attendance and punctuality during PIP period" },
      { id: 'pq_8', text: "Responsiveness to feedback and coaching" },
      { id: 'pq_9', text: "Collaboration and communication with team" }
    ]
  },
  {
    id: 'pip_4',
    title: "Support Utilization",
    description: "Evaluate how effectively the employee uses provided resources.",
    weight: 15,
    questions: [
      { id: 'pq_10', text: "Engagement with assigned training programs" },
      { id: 'pq_11', text: "Utilization of mentorship and check-in sessions" }
    ]
  },
  {
    id: 'pip_5',
    title: "Final Evaluation Criteria",
    description: "Overall assessment for PIP outcome determination.",
    weight: 10,
    questions: [
      { id: 'pq_12', text: "Overall readiness to return to standard performance expectations" },
      { id: 'pq_13', text: "Sustained improvement observed (not just during review periods)" }
    ]
  }
];

const PIP_RATING_OPTIONS = [
  "Significantly Exceeded",
  "Exceeded Target",
  "Met Target",
  "Partial Progress",
  "No Improvement"
];

const DEFAULT_EXIT_REASONS = [
  "Better Compensation",
  "Career Growth",
  "Higher Education",
  "Relocation",
  "Work-Life Balance",
  "Managerial Issues",
  "Work Culture",
  "Health / Personal",
  "Company Stability",
  "Other"
];

const DEFAULT_REVIEW_DATES = [
  { label: "Week 1 Check-in", daysFromStart: 7 },
  { label: "Week 2 Check-in", daysFromStart: 14 },
  { label: "Mid-PIP Review", daysFromStart: 15 },
  { label: "Final Review", daysFromStart: 30 },
];

// --- Template Preview Component ---

export const TemplatePreview = ({ title, sections, ratingOptions, type }) => {
  return (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto no-scrollbar">
      <div className="text-center pb-4 border-b">
        <h2 className="text-2xl font-bold text-foreground">{title}</h2>
        <p className="text-sm text-muted-foreground mt-1">Preview of the {type.toLowerCase()} form as seen by employees</p>
      </div>

      {sections.map((section, sIndex) => (
        <div key={section.id} className="border rounded-lg overflow-hidden">
          <div className="bg-primary/5 border-b px-4 py-3 flex justify-between items-center">
            <div>
              <h3 className="font-semibold text-primary">{section.title}</h3>
              {section.description && <p className="text-xs text-muted-foreground mt-0.5">{section.description}</p>}
            </div>
            <Badge variant="outline" className="text-xs">Weight: {section.weight}%</Badge>
          </div>
          <div className="divide-y">
            {section.questions.map((q, qIndex) => (
              <div key={q.id} className="p-4 space-y-3">
                <p className="text-sm font-medium">
                  <span className="text-muted-foreground mr-2">{qIndex + 1}.</span>
                  {q.text}
                </p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 pl-5">
                  {ratingOptions.map((opt, i) => (
                    <div key={i} className="flex items-center gap-2 border rounded px-3 py-2 bg-card text-xs text-muted-foreground">
                      <div className="h-3 w-3 rounded-full border-2 border-border shrink-0" />
                      <span>{opt}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-1 pl-5">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} size={14} className="text-muted-foreground/50" />
                  ))}
                  <span className="text-[10px] text-muted-foreground ml-1">Score</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="text-center pt-4 border-t text-xs text-muted-foreground">
        Total Weight: {sections.reduce((sum, s) => sum + (parseInt(s.weight) || 0), 0)}% &bull; {sections.reduce((sum, s) => sum + s.questions.length, 0)} Questions
      </div>
    </div>
  );
};

// --- Sortable Question Component ---
const SortableQuestion = ({ question, qIndex, sectionId, ratingOptions, onUpdate, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: question.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  return (
    <div ref={setNodeRef} style={style} className="flex gap-3 items-start group/question bg-muted/50 p-3 rounded-md hover:bg-muted transition-colors">
      <button {...attributes} {...listeners} className="mt-2.5 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-primary" data-testid={`drag-question-${question.id}`}>
        <GripVertical size={14} />
      </button>
      <div className="mt-2.5 text-xs font-medium text-muted-foreground w-4 text-center">{qIndex + 1}.</div>
      <div className="flex-1 space-y-3">
        <Input value={question.text} onChange={(e) => onUpdate(sectionId, question.id, e.target.value)} className="bg-transparent border-transparent hover:border-border focus-visible:bg-card focus-visible:border-border px-2 h-9" placeholder="Type your question here..." />
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 px-2">
          {ratingOptions.map((option, i) => (
            <div key={i} className="flex items-center gap-2 border rounded p-2 bg-card text-xs text-muted-foreground cursor-default opacity-70">
              <div className="h-3 w-3 rounded-full border border-border" />
              <span className="truncate">{option || `Option ${i + 1}`}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 px-2 pt-1">
          <Badge variant="outline" className="text-[10px] h-5 px-1 font-normal text-muted-foreground border-border bg-card">Score</Badge>
          <div className="flex gap-1">{[1, 2, 3, 4, 5].map((i) => (<Star key={i} size={16} className="text-amber-400 fill-amber-100" />))}</div>
        </div>
      </div>
      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-500 opacity-0 group-hover/question:opacity-100 transition-opacity" onClick={() => onDelete(sectionId, question.id)}>
        <Trash2 size={14} />
      </Button>
    </div>
  );
};

// --- Sortable Section Component ---
const SortableSection = ({ section, sensors, ratingOptions, onUpdateSection, onDeleteSection, onAddQuestion, onUpdateQuestion, onDeleteQuestion, onQuestionDragEnd }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 50 : 'auto' };

  return (
    <Card ref={setNodeRef} style={style} className="border-l-4 border-l-primary shadow-sm hover:shadow-md transition-shadow group" data-testid={`section-${section.id}`}>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <div className="flex items-start gap-2 flex-1 mr-4">
            <button {...attributes} {...listeners} className="mt-2 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-primary shrink-0" data-testid={`drag-section-${section.id}`}>
              <GripVertical size={18} />
            </button>
            <div className="space-y-1 flex-1">
              <Input value={section.title} onChange={(e) => onUpdateSection(section.id, 'title', e.target.value)} className="text-lg font-semibold border-transparent hover:border-border px-2 -ml-2 h-auto focus-visible:ring-1" placeholder="Section Title" />
              <Input value={section.description} onChange={(e) => onUpdateSection(section.id, 'description', e.target.value)} className="text-sm text-muted-foreground border-transparent hover:border-border px-2 -ml-2 h-8 focus-visible:ring-1" placeholder="Section Description (optional)" />
            </div>
          </div>
          <div className="flex items-center gap-2 mr-2">
            <Label className="text-xs text-muted-foreground whitespace-nowrap">Weight (%)</Label>
            <Input type="number" value={section.weight} onChange={(e) => onUpdateSection(section.id, 'weight', e.target.value)} className="w-16 h-8 text-left hide-spinner" min="0" max="100" />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity"><MoreVertical size={16} /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem className="text-red-600 focus:text-red-600" onClick={() => onDeleteSection(section.id)}>
                <Trash2 className="mr-2 h-4 w-4" /> Delete Section
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onQuestionDragEnd}>
          <SortableContext items={section.questions.map(q => q.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {section.questions.map((question, qIndex) => (
                <SortableQuestion key={question.id} question={question} qIndex={qIndex} sectionId={section.id} ratingOptions={ratingOptions} onUpdate={onUpdateQuestion} onDelete={onDeleteQuestion} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
        <Button variant="outline" size="sm" className="w-full border-dashed text-muted-foreground hover:text-primary hover:border-primary/50" onClick={() => onAddQuestion(section.id)}>
          <Plus size={14} className="mr-2" /> Add Question
        </Button>
      </CardContent>
    </Card>
  );
};

// --- Template Editor Component ---

const TemplateEditor = ({ initialTitle, initialSections, initialRatingOptions, type, editingTemplate, onTemplateSaved }) => {
  const [formTitle, setFormTitle] = useState(initialTitle);
  const [sections, setSections] = useState(initialSections);
  const [ratingOptions, setRatingOptions] = useState(initialRatingOptions);
  const [exitReasons, setExitReasons] = useState(type === 'Exit Interview' ? DEFAULT_EXIT_REASONS : []);
  const [reviewDates, setReviewDates] = useState(type === 'PIP' ? DEFAULT_REVIEW_DATES : []);
  const [templateId, setTemplateId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialData, setInitialData] = useState(null);


  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const totalWeight = sections.reduce((sum, section) => sum + (parseInt(section.weight) || 0), 0);

  // Load existing template from backend on mount

  useEffect(() => {
    // If editingTemplate is provided, load its data. This covers both an
    // existing template (has _id) and an imported draft (data but no _id).
    if (editingTemplate && (editingTemplate._id || editingTemplate.sections?.length)) {
      if (editingTemplate._id) setTemplateId(editingTemplate._id);
      if (editingTemplate.name) setFormTitle(editingTemplate.name);
      if (editingTemplate.sections?.length) setSections(editingTemplate.sections);
      if (editingTemplate.ratingOptions?.length) setRatingOptions(editingTemplate.ratingOptions);
      if (editingTemplate.exitReasons?.length) setExitReasons(editingTemplate.exitReasons);
      if (editingTemplate.reviewDates?.length) setReviewDates(editingTemplate.reviewDates);

      if (editingTemplate._id) {
        // Editing an existing template: track changes against its saved state.
        setInitialData({
          formTitle: editingTemplate.name,
          sections: editingTemplate.sections || [],
          ratingOptions: editingTemplate.ratingOptions || [],
          exitReasons: editingTemplate.exitReasons || [],
          reviewDates: editingTemplate.reviewDates || [],
        });
        setHasChanges(false);
      } else {
        // Imported draft: behave like a new template (save enabled, creates on save).
        setInitialData(null);
        setHasChanges(true);
      }
    } else {
      // When creating new template, start with hasChanges = true (save enabled)
      setInitialData(null);
      setHasChanges(true);
    }
    setLoaded(true);
  }, [type, editingTemplate]);

  useEffect(() => {
    // Only track changes after initial load is complete
    if (!loaded) return;

    // If there's no initial data (new template), always enabled
    if (!initialData) {
      setHasChanges(true);
      return;
    }

    // For existing templates, compare current state with initial data
    const hasTitleChanged = formTitle !== initialData.formTitle;
    const hasSectionsChanged = JSON.stringify(sections) !== JSON.stringify(initialData.sections);
    const hasRatingOptionsChanged = JSON.stringify(ratingOptions) !== JSON.stringify(initialData.ratingOptions);
    const hasExitReasonsChanged = JSON.stringify(exitReasons) !== JSON.stringify(initialData.exitReasons);
    const hasReviewDatesChanged = JSON.stringify(reviewDates) !== JSON.stringify(initialData.reviewDates);

    setHasChanges(
      hasTitleChanged ||
      hasSectionsChanged ||
      hasRatingOptionsChanged ||
      hasExitReasonsChanged ||
      hasReviewDatesChanged
    );
  }, [formTitle, sections, ratingOptions, exitReasons, reviewDates, loaded, initialData]);

  const validateTemplate = () => {
    if (!sections.length) {
      return "At least one section is required.";
    }

    let total = 0;

    for (let i = 0; i < sections.length; i++) {
      const weight = sections[i].weight;

      if (weight === "" || weight === null || weight === undefined) {
        return `Section ${i + 1} has no weight.`;
      }

      const numWeight = Number(weight);

      if (isNaN(numWeight)) {
        return `Section ${i + 1} has invalid weight.`;
      }

      if (!Number.isInteger(numWeight)) {
        return `Section ${i + 1} cannot have decimal weight.`;
      }

      if (numWeight <= 0) {
        return `Section ${i + 1} must have weight greater than 0.`;
      }

      total += numWeight;
    }

    if (total !== 100) {
      return `Total weight must be exactly 100%. Current total is ${total}%.`;
    }

    return null;
  };

  const handleSave = async () => {
    const error = validateTemplate();

    if (error) {
      toast.error(error);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formTitle,
        type,
        sections,
        ratingOptions,
        exitReasons: type === 'Exit Interview' ? exitReasons : [],
        reviewDates: type === 'PIP' ? reviewDates : [],
      };

      if (templateId) {
        await updateTemplate(templateId, payload);
        toast.success(`${type} template updated!`);
      } else {
        const response = await createTemplate(payload);
        const created = response.data;
        setTemplateId(created._id);
        toast.success(`${type} template created!`);
      }
      if (onTemplateSaved) onTemplateSaved();
    } catch (err) {
      const errorMessage = err?.response?.data?.error || err.message || "Failed to save template";
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleSectionDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setSections((prev) => {
        const oldIdx = prev.findIndex(s => s.id === active.id);
        const newIdx = prev.findIndex(s => s.id === over.id);
        return arrayMove(prev, oldIdx, newIdx);
      });
    }
  };

  const handleQuestionDragEnd = (sectionId) => (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setSections((prev) => prev.map(s => {
        if (s.id !== sectionId) return s;
        const oldIdx = s.questions.findIndex(q => q.id === active.id);
        const newIdx = s.questions.findIndex(q => q.id === over.id);
        return { ...s, questions: arrayMove(s.questions, oldIdx, newIdx) };
      }));
    }
  };

  const addSection = () => {
    const newSection = {
      id: Math.random().toString(36).substr(2, 9),
      title: "New Section",
      description: "",
      weight: 0,
      questions: [{ id: Math.random().toString(36).substr(2, 9), text: "New Question" }]
    };
    setSections([...sections, newSection]);
  };

  const deleteSection = (sectionId) => {
    setSections(sections.filter(s => s.id !== sectionId));
    toast.success("Section deleted");
  };

  const updateSection = (sectionId, field, value) => {
    setSections(sections.map(s => s.id === sectionId ? { ...s, [field]: value } : s));
  };

  const addQuestion = (sectionId) => {
    const newQuestion = {
      id: Math.random().toString(36).substr(2, 9),
      text: ""
    };
    setSections(sections.map(s => {
      if (s.id === sectionId) {
        return { ...s, questions: [...s.questions, newQuestion] };
      }
      return s;
    }));
  };

  const deleteQuestion = (sectionId, questionId) => {
    setSections(sections.map(s => {
      if (s.id === sectionId) {
        return { ...s, questions: s.questions.filter(q => q.id !== questionId) };
      }
      return s;
    }));
  };

  const updateQuestion = (sectionId, questionId, text) => {
    setSections(sections.map(s => {
      if (s.id === sectionId) {
        return {
          ...s,
          questions: s.questions.map(q => q.id === questionId ? { ...q, text } : q)
        };
      }
      return s;
    }));
  };

  const updateRatingOption = (index, value) => {
    const newOptions = [...ratingOptions];
    newOptions[index] = value;
    setRatingOptions(newOptions);
  };

  const validateError = validateTemplate();

  return (
    <div className="space-y-8 pb-20">
      {/* Header & Global Settings */}
      <div className="flex flex-col md:flex-row gap-6 items-start justify-between">
        <div className="flex-1 space-y-2">
          <Input
            value={formTitle}
            onChange={(e) => setFormTitle(e.target.value)}
            className="text-3xl font-bold border-transparent hover:border-border px-2 h-auto focus-visible:ring-0 text-foreground placeholder:text-muted-foreground"
            placeholder="Form Title"
          />
          <p className="text-muted-foreground">Customize your {type.toLowerCase()} template.</p>
        </div>
        <div className="flex gap-2 items-center">
          <Badge variant={totalWeight === 100 ? "outline" : "destructive"} className={totalWeight === 100 ? "border-green-500 text-green-600 bg-green-50 dark:border-green-700 dark:text-green-400 dark:bg-green-950/30" : ""}>
            Total Weight: {totalWeight}%
          </Badge>
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="preview-template-btn">
                <Eye size={16} /> Preview
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto no-scrollbar">
              <DialogHeader>
                <DialogTitle>Form Preview</DialogTitle>
              </DialogHeader>
              <TemplatePreview
                title={formTitle}
                sections={sections}
                ratingOptions={ratingOptions}
                type={type}
              />
            </DialogContent>
          </Dialog>
          <Button className="gap-2 bg-primary hover:bg-primary/90" onClick={handleSave} disabled={saving || !hasChanges} data-testid="save-template-btn">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} {saving ? "Saving..." : hasChanges ? "Save Changes" : "No Changes"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

        {/* Main Form Area - Independent Scrolling */}
        <div className="lg:col-span-8 space-y-6 overflow-y-auto pr-2 no-scrollbar pb-8" style={{ maxHeight: 'calc(95vh - 200px)' }}>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSectionDragEnd}>
            <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
              {sections.map((section) => (
                <SortableSection
                  key={section.id}
                  section={section}
                  sensors={sensors}
                  ratingOptions={ratingOptions}
                  onUpdateSection={updateSection}
                  onDeleteSection={deleteSection}
                  onAddQuestion={addQuestion}
                  onUpdateQuestion={updateQuestion}
                  onDeleteQuestion={deleteQuestion}
                  onQuestionDragEnd={handleQuestionDragEnd(section.id)}
                />
              ))}
            </SortableContext>
          </DndContext>

          <Button
            variant="outline"
            className="w-full py-8 border-dashed text-lg font-medium text-muted-foreground hover:text-primary hover:border-primary hover:bg-muted"
            onClick={addSection}
          >
            <Plus size={20} className="mr-2" /> Add New Section
          </Button>
        </div>

        {/* Right Sidebar - Configuration - Independent Scrolling */}
        <div className="lg:col-span-4 space-y-6 overflow-y-auto pr-2 no-scrollbar pb-8" style={{ maxHeight: 'calc(95vh - 200px)' }}>
          <Card>
            <CardHeader className="pb-3 border-b bg-muted/50">
              <CardTitle className="text-base font-semibold">Rating Configuration</CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="space-y-1">
                <Label className="text-xs font-medium text-muted-foreground uppercase">Rating Scale Options</Label>
                <p className="text-xs text-muted-foreground mb-2">These options will apply to all questions in the form.</p>
                <div className="space-y-2">
                  {ratingOptions.map((option, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="text-xs font-mono text-muted-foreground w-4">{index + 1}</span>
                      <Input
                        value={option}
                        onChange={(e) => updateRatingOption(index, e.target.value)}
                        className="h-8 text-sm"
                        placeholder={`Option ${index + 1}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              <div className="space-y-3">
                <Label className="text-xs font-medium text-muted-foreground uppercase">Scoring Logic</Label>
                <div className="p-3 bg-muted rounded text-xs text-muted-foreground space-y-2">
                  <p>
                    <strong>Final Score Calculation:</strong>
                  </p>
                  <p>
                    (Average Star Rating × Section Weight)
                  </p>
                  <div className="flex justify-between font-medium pt-2 border-t">
                    <span>Total Score:</span>
                    <span>Sum of Weighted Averages</span>
                  </div>
                </div>
              </div>

              {/* Exit Reasons (Exit Interview only) */}
              {type === 'Exit Interview' && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-muted-foreground uppercase">Reasons for Leaving</Label>
                    <p className="text-xs text-muted-foreground mb-2">Options shown to the employee for why they are leaving.</p>
                    <div className="space-y-2">
                      {exitReasons.map((reason, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <span className="text-xs font-mono text-muted-foreground w-4">{idx + 1}</span>
                          <Input
                            value={reason}
                            onChange={(e) => {
                              const updated = [...exitReasons];
                              updated[idx] = e.target.value;
                              setExitReasons(updated);
                            }}
                            className="h-8 text-sm"
                            placeholder={`Reason ${idx + 1}`}
                            data-testid={`exit-reason-input-${idx}`}
                          />
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:bg-destructive/10 shrink-0"
                            onClick={() => setExitReasons(exitReasons.filter((_, i) => i !== idx))} disabled={exitReasons.length <= 1}>
                            <Trash2 size={12} />
                          </Button>
                        </div>
                      ))}
                    </div>
                    <Button variant="outline" size="sm" className="w-full text-xs gap-1" onClick={() => setExitReasons([...exitReasons, ""])} data-testid="add-exit-reason-btn">
                      <Plus size={12} /> Add Reason
                    </Button>
                  </div>
                </>
              )}

              {/* Review Milestones removed from builder — milestones are per-PIP, set during creation */}

            </CardContent>
          </Card>
        </div>

      </div>
    </div>
  );
};

// --- Main Wrapper Component ---

const FormBuilder = ({ templateType, editingTemplate, onTemplateSaved }) => {
  return (
    <div className="w-full">
      {templateType === "Appraisal" && (
        <TemplateEditor
          type="Appraisal"
          initialTitle="Employee Appraisal Form"
          initialSections={APPRAISAL_DEFAULT_SECTIONS}
          initialRatingOptions={APPRAISAL_RATING_OPTIONS}
          editingTemplate={editingTemplate?.type === "Appraisal" ? editingTemplate : null}
          key={`appraisal-${editingTemplate?.type === "Appraisal" ? editingTemplate._id : "new"}`}
          onTemplateSaved={onTemplateSaved}
        />
      )}

      {templateType === "Exit Interview" && (
        <TemplateEditor
          type="Exit Interview"
          initialTitle="Exit Interview Template"
          initialSections={EXIT_DEFAULT_SECTIONS}
          initialRatingOptions={EXIT_RATING_OPTIONS}
          editingTemplate={editingTemplate?.type === "Exit Interview" ? editingTemplate : null}
          key={`exit-${editingTemplate?.type === "Exit Interview" ? editingTemplate._id : "new"}`}
          onTemplateSaved={onTemplateSaved}
        />
      )}

      {templateType === "PIP" && (
        <TemplateEditor
          type="PIP"
          initialTitle="Performance Improvement Plan Template"
          initialSections={PIP_DEFAULT_SECTIONS}
          initialRatingOptions={PIP_RATING_OPTIONS}
          editingTemplate={editingTemplate?.type === "PIP" ? editingTemplate : null}
          key={`pip-${editingTemplate?.type === "PIP" ? editingTemplate._id : "new"}`}
          onTemplateSaved={onTemplateSaved}
        />
      )}
    </div>
  );
};

export default FormBuilder;
